import type {FeatureFlagKey} from "@mytour/shared";
import posthog from "posthog-js";
import {useSyncExternalStore} from "react";
import {isAnalyticsInitialized, isCaptureEnabled} from "./analytics.js";
import {authenticationApiService} from "./authentication/authenticationApi.service.js";

/**
 * Reads a PostHog feature flag reactively. Returns `true` only when the flag
 * resolves to enabled for the current person; everything else (PostHog
 * disabled, flags not loaded yet, flag absent) defaults to `false` — gated UI
 * stays hidden until a flag positively turns it on.
 *
 * Local/e2e override (#633): on localhost ONLY, a localStorage entry
 * `feature-flag-override:<key>` = `"true" | "false"` short-circuits PostHog —
 * see `getLocalFeatureFlagOverride`. Deployed environments never read it.
 *
 * Reactive by design: it subscribes to `posthog.onFeatureFlags`, which fires
 * both on the initial load and on every reload — notably right after
 * `identify` (see analyticsSession), so a flag targeting a person property
 * unlocks the UI without waiting for a page refresh.
 *
 * The keys are typed against the `@mytour/shared` registry so a typo can't
 * silently evaluate to a non-existent flag (always `false`).
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useSyncExternalStore(subscribeFeatureFlags, () => getFeatureFlagSnapshot(key));
}

/**
 * Current enabled state of a flag, read synchronously from the SDK. Pure
 * relative to PostHog state: same SDK state → same boolean, so it is safe as a
 * `useSyncExternalStore` snapshot. `isFeatureEnabled` returns `undefined`
 * before flags load — coalesced to `false`.
 *
 * `send_event` follows the capture gate AND the moderation gate: the
 * `$feature_flag_called` exposure event fires only on capture environments
 * (prod/dev) and never in a moderation session — keeping flags-only
 * environments (canary/latest) and support impersonation free of SDK events,
 * consistent with `track` and `identify`. The flag is still **evaluated**
 * (targeting keeps working in moderation); only the exposure event is cut.
 */
export function getFeatureFlagSnapshot(key: FeatureFlagKey): boolean {
  const override = getLocalFeatureFlagOverride(key);
  if (override != null) {
    return override;
  }
  if (!isAnalyticsInitialized()) {
    return false;
  }
  const sendEvent = isCaptureEnabled() && !authenticationApiService.isModeration();
  return posthog.isFeatureEnabled(key, {send_event: sendEvent}) ?? false;
}

/**
 * Deterministic local/e2e override of a feature flag (#633), so gated pages
 * (e.g. `/rdv-requests`) can be QA'd against a local `yarn dev` stack where
 * PostHog flags don't target the throwaway accounts.
 *
 * Honored on **localhost / 127.0.0.1 only** — a deployed environment (prod,
 * canary, latest) never reads it, so production rollout gating is untouched.
 * Usage (browser console or Playwright `addInitScript`), then reload:
 *
 *   localStorage.setItem("feature-flag-override:rdv-en-ligne", "true")
 *
 * `"false"` forces the flag OFF (QA of the gated-off behavior); any other
 * value — or no entry — falls through to the normal PostHog evaluation. Read
 * per snapshot, not subscribed: setting it in the same tab needs a reload,
 * which is the QA workflow anyway.
 */
export function getLocalFeatureFlagOverride(key: FeatureFlagKey): boolean | null {
  if (typeof window === "undefined" || !isLocalhost(window.location.hostname)) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(`feature-flag-override:${key}`);
  } catch {
    // Storage can throw (privacy mode) — treat as "no override".
    return null;
  }
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  return null;
}

/**
 * The local/e2e context: the Vite dev server and Playwright both browse
 * `localhost` (or `127.0.0.1`). Deliberately NOT extended to other loopbacks
 * (`[::1]`, `*.localhost`) until someone actually browses through them — the
 * override silently no-ops there, never on a deployed host.
 */
function isLocalhost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Subscribes to PostHog flag (re)loads. Returns the unsubscribe handle;
 * `onFeatureFlags` returns one in current posthog-js, but we guard the type so
 * a version that returns void can't break cleanup. A no-op subscription when
 * PostHog is disabled keeps the snapshot a stable `false`.
 */
export function subscribeFeatureFlags(onChange: () => void): () => void {
  if (!isAnalyticsInitialized()) {
    return () => {};
  }
  const unsubscribe = posthog.onFeatureFlags(() => onChange());
  return typeof unsubscribe === "function" ? unsubscribe : () => {};
}
